/** Session choices for the view controls the screen makes selectable, and the query knobs they map to. */

import { useCallback, useMemo, useState } from 'react';
import type { ViewControl } from 'src/components/star-grid/api/reportEngineTypes';
import type { ChoiceOption } from 'src/components/star-grid/utils/screenOptions';
import { screenControlKnobs, selectableViewControls, type ScreenControlKnobs } from 'src/components/star-grid/utils/screenViewControls';

export interface ViewControlAction {
  readonly options: readonly ChoiceOption[];
  readonly onChange: (value: string) => void;
  readonly value?: string;
}

export interface UseScreenViewControlsResult {
  /** Selector wiring per control id, for the bar. Only controls the screen makes selectable appear. */
  readonly actions: Readonly<Record<string, ViewControlAction>>;
  readonly knobs: ScreenControlKnobs;
  readonly isSelectable: (controlId: string) => boolean;
}

export const useScreenViewControls = (controls: readonly ViewControl[] | undefined): UseScreenViewControlsResult => {
  const [chosen, setChosen] = useState<Readonly<Record<string, string>>>({});
  const selectable = useMemo(() => selectableViewControls(controls ?? []), [controls]);
  const actions = useMemo(
    () =>
      Object.fromEntries(
        selectable.map(({ control, options, defaultValue }) => [
          control.id,
          {
            options,
            onChange: (value: string) => setChosen((was) => ({ ...was, [control.id]: value })),
            value: chosen[control.id] ?? defaultValue
          }
        ])
      ),
    [selectable, chosen]
  );
  const knobs = useMemo(() => screenControlKnobs(selectable, chosen), [selectable, chosen]);
  const ids = useMemo(() => new Set(selectable.map(({ control }) => control.id)), [selectable]);
  const isSelectable = useCallback((controlId: string) => ids.has(controlId), [ids]);
  return { actions, knobs, isSelectable };
};
